import { Button, Typography, Stack, Paper, alpha } from '@mui/material'

import { MESSAGES } from '@/constants/messages'
import { useActiveSession } from '@/contexts/ActiveSessionContext'
import EllipsisLoading from '@/components/EllipsisLoading'

import { ChargingSessionInAppNotificationStatus } from '@/lib/in-app-notifications/categories/charging_session'

import theme from '@/theme'
import { chargingUtils } from '@/utils/charging'

import ChargingPointScreenTemplate from './template/ChargingPointScreenTemplate'
import { ChargingPointScreenProps } from './types'

import ChargingLiveStatus from '../components/ChargingLiveStatus'
import ChargingSessionSummary from '../components/ChargingSessionSummary'
import { useChargingTimer } from '../hooks/useChargingTimer'

const InProgressChargingSessionScreen: React.FC<ChargingPointScreenProps> = (props) => {
  const activeSession = useActiveSession()

  const { notification, connected } = activeSession
  const payload = notification?.data?.payload

  const { elapsedTime } = useChargingTimer({
    startTime: chargingUtils.getChargeStartTime(payload),
    endTime: payload?.charge_finished_at,
    serverNow: notification?.data?.server_time_now,
  })

  const { elapsedTime: idleElapsedTime } = useChargingTimer({
    startTime: payload?.idle_start_time,
    serverNow: notification?.data?.server_time_now,
  })

  const isIdle = payload?.status === ChargingSessionInAppNotificationStatus.IDLE

  return (
    <ChargingPointScreenTemplate
      {...props}
      Body={
        <Stack width="100%" height="100%" maxWidth={400} direction="column" spacing={2} alignItems="center" py={1}>
          {!connected && (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'warning.light',
                borderRadius: 2,
                padding: 1,
                width: '100%',
              }}
            >
              <Typography
                variant="caption"
                textAlign="center"
                color="warning.contrastText"
                display="block"
                fontWeight="bold"
              >
                {MESSAGES.CHARGING_SESSION.OFFLINE_RECONNECTING}
                <EllipsisLoading color="white" />
              </Typography>
            </Paper>
          )}
          <Stack flex={1} justifyContent="center" alignItems="center" width="100%" spacing={3}>
            <ChargingSessionSummary
              elapsedTime={elapsedTime}
              idleElapsedTime={idleElapsedTime}
              kwhDelivered={payload?.kwh_delivered}
              totalCost={payload?.total_cost}
              idleCost={payload?.idle_cost}
            />
            <ChargingLiveStatus soc={payload?.soc} powerActive={payload?.power_active} />
          </Stack>
          {isIdle && (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: alpha(theme.palette.warning.light, 0.2),
                border: `1px solid ${theme.palette.warning.main}`,
                borderRadius: 2,
                padding: 2,
                width: '100%',
              }}
            >
              <Stack direction="column">
                <Typography variant="body2" color="text.primary" fontWeight="bold" textAlign="center">
                  {MESSAGES.CHARGING_SESSION.IN_IDLE}
                </Typography>
                <Typography variant="caption" textAlign="center" color="text.secondary">
                  {MESSAGES.CHARGING_SESSION.IDLE_WARNING}
                </Typography>
              </Stack>
            </Paper>
          )}
        </Stack>
      }
      Footer={
        !isIdle && (
          <Button
            color="error"
            variant="contained"
            sx={{ width: '100%', borderRadius: 4 }}
            onClick={() => props.onStopCharging?.()}
            disableElevation
          >
            Finalizar carregamento
          </Button>
        )
      }
    />
  )
}

export default InProgressChargingSessionScreen
